import React, { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CalendarIcon } from '@/../public/assets/icons';
import { createPoliceEncounter } from '@/services/incident/incident.service';
import { policeEncounterSchema, TPoliceEncounter } from './police-encounter.validator';
import { manageProfileStyles } from '@/components/layouts/landing/ManageProfile/manageProfile.styles';
import { Box, Button, Image, Stack, Text, Dialog, Field, Input, Textarea, Checkbox, Alert } from '@chakra-ui/react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import BarLoader from '@/components/ui/loaders/BarLoader';
import { toaster } from '@/components/ui';

const PoliceEncounterForm = ({ onClose }: { onClose: () => void }) => {
   const dateInput = useRef<HTMLInputElement | null>(null);
   const {
      register,
      handleSubmit,
      formState: { errors, isSubmitting },
      setError,
      setValue,
      watch,
   } = useForm<TPoliceEncounter>({
      defaultValues: { card_owner: true, nfa: true },
      resolver: zodResolver(policeEncounterSchema),
   });

   const { phone_number, contact_phone, card_owner, nfa } = watch();

   const onSubmit = handleSubmit(async value => {
      console.log(value);

      const payload = {
         police: {
            name: value.name,
            badge: value.badge,
            force: value.force,
            phone_number: value.phone_number,
         },
         reason: value.reason,
         description: value.description,
         card_owner: value.card_owner,
         nfa: value.nfa,
         contact_email: value.contact_email,
         contact_phone: value.contact_phone,
         timestamp: value.timestamp,
      };

      const [error] = await createPoliceEncounter(payload);

      if (error) {
         console.log(error);
         setError('root', { message: error.response?.data.message as string, type: 'manual' });
         return;
      }

      toaster.success({ title: 'Your report has been submitted' });

      onClose();
   });

   const handleDateInput = () => {
      dateInput.current?.showPicker();
   };

   return (
      <form onSubmit={onSubmit}>
         <Dialog.Body>
            {isSubmitting && <BarLoader loadingText="Submitting..." />}
            <Box w="16" h="16" rounded="50%" m="0 auto 10px" display="flex" alignItems="center" justifyContent="center">
               <Image src="/assets/images/police-encounter.png" alt="" width="26" height="26" />
            </Box>
            <Dialog.Title color="white" textStyle="4xl" textAlign="center" mb="2">
               Report a Police Encounter
            </Dialog.Title>
            <Text color="darkGray" textStyle="xs" textAlign="center" mb="4">
               Please let us know if you have encountered a police stop, every bit of this feedback helps us to identify
               any areas where extra or specific training may be required.
            </Text>
            {errors.root?.message && (
               <Alert.Root status="error">
                  <Alert.Indicator />
                  <Alert.Title>{errors.root.message}</Alert.Title>
               </Alert.Root>
            )}
            <Stack>
               <Box css={manageProfileStyles.double_input}>
                  <Field.Root invalid={!!errors.name}>
                     <Field.Label textStyle="xs" color="white" mb="1">
                        Officer&apos;s Name
                        <Box as="span" color="error">
                           *
                        </Box>
                     </Field.Label>
                     <Input
                        placeholder="Officer's Name"
                        variant="subtle"
                        h="10"
                        rounded="12px"
                        bg="#414141"
                        color="#ffffff"
                        _placeholder={{ color: '#7A7A7A' }}
                        {...register('name')}
                     />
                     {errors.name && <Field.ErrorText>{errors.name.message}</Field.ErrorText>}
                  </Field.Root>
                  <Field.Root invalid={!!errors.badge}>
                     <Field.Label textStyle="xs" color="white" mb="1">
                        Officer&apos;s Badge Number
                        <Box as="span" color="error">
                           *
                        </Box>
                     </Field.Label>
                     <Input
                        placeholder="Officer' Badge number"
                        variant="subtle"
                        h="10"
                        rounded="12px"
                        bg="#414141"
                        color="#ffffff"
                        _placeholder={{ color: '#7A7A7A' }}
                        {...register('badge')}
                     />
                     {errors.badge && <Field.ErrorText>{errors.badge.message}</Field.ErrorText>}
                  </Field.Root>
               </Box>
               <Box css={manageProfileStyles.double_input}>
                  <Field.Root invalid={!!errors.force}>
                     <Field.Label textStyle="xs" color="white" mb="1">
                        Officer&apos;s Force
                        <Box as="span" color="error">
                           *
                        </Box>
                     </Field.Label>
                     <Input
                        placeholder="Officer's Force"
                        variant="subtle"
                        h="10"
                        rounded="12px"
                        bg="#414141"
                        color="#ffffff"
                        _placeholder={{ color: '#7A7A7A' }}
                        {...register('force')}
                     />
                     {errors.force && <Field.ErrorText>{errors.force.message}</Field.ErrorText>}
                  </Field.Root>
                  <Field.Root invalid={!!errors.phone_number}>
                     <Field.Label textStyle="xs" color="white" mb="1">
                        Officer&apos;s Phone Number
                        <Box as="span" color="error">
                           *
                        </Box>
                     </Field.Label>
                     <PhoneInput
                        country={'gb'}
                        preferredCountries={['gb', 'us']}
                        placeholder="Phone Number"
                        enableSearch
                        value={phone_number}
                        onChange={v => setValue('phone_number', v)}
                        inputStyle={{
                           width: '100%',
                           height: '40px',
                           background: '#414141',
                           borderRadius: '12px',
                           border: errors.phone_number?.message ? '1px solid #f87171' : 'none',
                           color: 'white',
                           paddingLeft: '46px',
                           fontSize: '14px',
                        }}
                        dropdownStyle={{
                           backgroundColor: '#000',
                           color: 'white',
                        }}
                     />
                     {errors.phone_number && <Field.ErrorText>{errors.phone_number.message}</Field.ErrorText>}
                  </Field.Root>
               </Box>
               <Field.Root invalid={!!errors.reason}>
                  <Field.Label textStyle="xs" color="white" mb="1">
                     Reason for the stop
                     <Box as="span" color="error">
                        *
                     </Box>
                  </Field.Label>
                  <Input
                     placeholder="Write reason"
                     variant="subtle"
                     h="10"
                     rounded="12px"
                     bg="#414141"
                     color="#ffffff"
                     _placeholder={{ color: '#7A7A7A' }}
                     {...register('reason')}
                  />
                  {errors.reason && <Field.ErrorText>{errors.reason.message}</Field.ErrorText>}
               </Field.Root>
               <Field.Root invalid={!!errors.description}>
                  <Field.Label textStyle="xs" color="white" mb="1">
                     What happened?
                     <Box as="span" color="error">
                        *
                     </Box>
                  </Field.Label>
                  <Textarea
                     placeholder="Give a full description of exactly what happened with as much detail as possible"
                     variant="subtle"
                     rows={4}
                     h="20"
                     rounded="12px"
                     bg="#414141"
                     color="#ffffff"
                     _placeholder={{ color: '#7A7A7A' }}
                     {...register('description')}
                  />
                  {errors.description && <Field.ErrorText>{errors.description.message}</Field.ErrorText>}
               </Field.Root>
               <Field.Root invalid={!!errors.contact_email}>
                  <Field.Label textStyle="xs" color="white" mb="1">
                     Contact Email
                     <Box as="span" color="error">
                        *
                     </Box>
                  </Field.Label>
                  <Input
                     placeholder="Email address"
                     variant="subtle"
                     h="10"
                     rounded="12px"
                     bg="#414141"
                     color="#ffffff"
                     _placeholder={{ color: '#7A7A7A' }}
                     {...register('contact_email')}
                  />
                  {errors.contact_email && <Field.ErrorText>{errors.contact_email.message}</Field.ErrorText>}
               </Field.Root>
               <Box css={manageProfileStyles.double_input}>
                  <Field.Root invalid={!!errors.contact_phone}>
                     <Field.Label textStyle="xs" color="white" mb="1">
                        Contact Phone Number
                        <Box as="span" color="error">
                           *
                        </Box>
                     </Field.Label>
                     <PhoneInput
                        country={'gb'}
                        preferredCountries={['gb', 'us']}
                        placeholder="Phone Number"
                        enableSearch
                        value={contact_phone}
                        onChange={v => setValue('contact_phone', v)}
                        inputStyle={{
                           width: '100%',
                           height: '40px',
                           background: '#414141',
                           borderRadius: '12px',
                           border: errors.phone_number?.message ? '1px solid #f87171' : 'none',
                           color: 'white',
                           paddingLeft: '46px',
                           fontSize: '14px',
                        }}
                        dropdownStyle={{
                           backgroundColor: '#000',
                           color: 'white',
                        }}
                     />
                     {errors.contact_phone && <Field.ErrorText>{errors.contact_phone.message}</Field.ErrorText>}
                  </Field.Root>
                  <Field.Root invalid={!!errors.timestamp}>
                     <Field.Label textStyle="xs" color="white" mb="1">
                        When?
                     </Field.Label>
                     <Box position="relative" w="full" onClick={handleDateInput}>
                        <Box css={manageProfileStyles.icon_box}>
                           <CalendarIcon />
                        </Box>
                        <Input
                           css={manageProfileStyles.date_input}
                           type="date"
                           placeholder="Select"
                           variant="subtle"
                           h="10"
                           rounded="12px"
                           bg="#414141"
                           color="#ffffff"
                           _placeholder={{ color: '#7A7A7A' }}
                           {...register('timestamp')}
                           ref={e => {
                              register('timestamp').ref(e);
                              dateInput.current = e;
                           }}
                        />
                     </Box>
                     {errors.timestamp && <Field.ErrorText>{errors.timestamp.message}</Field.ErrorText>}
                  </Field.Root>
               </Box>
               <Checkbox.Root
                  cursor="pointer"
                  checked={card_owner}
                  onCheckedChange={e => setValue('card_owner', !!e.checked)}
               >
                  <Checkbox.HiddenInput />
                  <Checkbox.Control />
                  <Checkbox.Label color="#ffffff">I hold a valid and up - to - date Cancard</Checkbox.Label>
               </Checkbox.Root>

               <Checkbox.Root cursor="pointer" checked={nfa} onCheckedChange={e => setValue('nfa', !!e.checked)}>
                  <Checkbox.HiddenInput />
                  <Checkbox.Control />
                  <Checkbox.Label color="#ffffff">NFA (No Further Action)</Checkbox.Label>
               </Checkbox.Root>
            </Stack>
         </Dialog.Body>
         <Dialog.Footer justifyContent="center" pt="0">
            <Button
               variant="outline"
               h="10"
               w="120px"
               bg="darkerBtn"
               color="white"
               border="none"
               textStyle="xs"
               fontWeight="600"
               onClick={onClose}
            >
               Cancel
            </Button>

            <Button
               type="submit"
               variant="outline"
               h="10"
               w="120px"
               border="none"
               textStyle="xs"
               fontWeight="600"
               disabled={isSubmitting}
            >
               Submit
            </Button>
         </Dialog.Footer>
      </form>
   );
};

export default PoliceEncounterForm;
